import { trackEvent } from "@/lib/analytics";
import type { EntryPointConfig, OpportunityPayload } from "@/lib/opportunityPayload";
import {
  APP_RESCUE_CONVERSION_GOAL,
  PUBLIC_CONVERSION_EXPERIMENT,
} from "@/lib/publicFunnelConstants";

type EventProps = Record<string, string | number | boolean | undefined>;

export { APP_RESCUE_CONVERSION_GOAL, PUBLIC_CONVERSION_EXPERIMENT };

export type AppRescueFunnelStep =
  | "intake_started"
  | "situation_completed"
  | "contact_viewed"
  | "consent_checked"
  | "submit_attempted"
  | "operator_opportunity_created"
  | "diagnostic_booking_clicked"
  | "submit_failed";

const serviceByPath: Record<string, string> = {
  "/ai-operators": "ai_operator",
  "/app-build": "app_build",
  "/app-rescue": "app_rescue",
  "/contact": "contact",
  "/project-planner": "project_translator",
  "/systems-audit": "systems_audit",
  "/venture-evaluation": "venture_evaluation",
  "/work": "work",
};

const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export interface CtaClickInput {
  label: string;
  destination: string;
  placement: string;
  page?: string;
  service?: string;
}

export function trackCtaClick(input: CtaClickInput): void {
  trackEvent("cta_clicked", buildCtaClickedProps(input));
}

export function trackPublicFunnelEvent(
  name: string,
  props: EventProps = {},
  pageUrl?: string
): void {
  trackEvent(name, withPublicFunnelContext(props, pageUrl));
}

export function trackAppRescueFunnelStep(
  funnelStep: AppRescueFunnelStep,
  props: EventProps = {},
  pageUrl?: string
): void {
  trackEvent("app_rescue_funnel_step", buildAppRescueFunnelStepProps(funnelStep, props, pageUrl));
}

export function buildAppRescueFunnelStepProps(
  funnelStep: AppRescueFunnelStep,
  props: EventProps = {},
  pageUrl?: string
): EventProps {
  return withPublicFunnelContext(
    {
      service: "app_rescue",
      entry_point: "rescue_finish_app",
      route: "/app-rescue",
      conversion_goal: APP_RESCUE_CONVERSION_GOAL,
      funnel_step: funnelStep,
      ...props,
    },
    pageUrl,
    "/app-rescue"
  );
}

export function buildCtaClickedProps(input: CtaClickInput, pageUrl?: string): EventProps {
  const destination = normalizePath(input.destination);
  const page = input.page || readPath(pageUrl);

  return withPublicFunnelContext(
    {
      label: input.label,
      placement: input.placement,
      destination,
      service: input.service || serviceForPath(destination) || serviceForPath(page),
    },
    pageUrl,
    page
  );
}

export function buildIntakeEventProps(
  config: EntryPointConfig,
  extra: EventProps = {},
  pageUrl?: string
): EventProps {
  return withPublicFunnelContext(
    {
      entry_point: config.entryPoint,
      service: config.opportunityType,
      route: config.path,
      ...extra,
    },
    pageUrl
  );
}

export function buildIntakeSubmissionProps(
  config: EntryPointConfig,
  payload: OpportunityPayload,
  resultMode: string,
  duplicate?: boolean,
  pageUrl?: string
): EventProps {
  return buildIntakeEventProps(
    config,
    {
      mode: resultMode,
      duplicate,
      urgency: normalizeAnalyticsValue(payload.request.urgency),
      has_timeline: Boolean(payload.request.timeline),
      has_budget_range: Boolean(payload.request.budgetRange),
      has_company: Boolean(payload.contact.company),
      has_website: Boolean(payload.business.website),
      has_project_translator_plan: Boolean(payload.details.projectPlanRecommendation),
      qualification_score: payload.qualification.score ?? undefined,
      has_utm: Object.keys(payload.context.utm).length > 0,
    },
    pageUrl
  );
}

export function withPublicFunnelContext(
  props: EventProps,
  pageUrl?: string,
  explicitPage?: string
): EventProps {
  const page = explicitPage || readPath(pageUrl);

  return {
    experiment: PUBLIC_CONVERSION_EXPERIMENT,
    page,
    ...utmProps(pageUrl),
    ...props,
  };
}

export function serviceForPath(path: string): string | undefined {
  const normalized = normalizePath(path);
  return serviceByPath[normalized];
}

export function utmProps(pageUrl?: string): EventProps {
  const url = readUrl(pageUrl);
  if (!url) return {};

  return Object.fromEntries(
    utmKeys
      .map((key) => [key, url.searchParams.get(key)?.trim()] as const)
      .filter(([, value]) => Boolean(value))
  );
}

function readPath(pageUrl?: string): string {
  const url = readUrl(pageUrl);
  if (url) return normalizePath(url.pathname);
  if (typeof window === "undefined") return "/";

  return normalizePath(window.location.pathname);
}

function readUrl(pageUrl?: string): URL | null {
  if (pageUrl) {
    try {
      return new URL(pageUrl, "https://shockvaluemedia.com");
    } catch {
      return null;
    }
  }

  if (typeof window === "undefined") {
    return null;
  }

  return new URL(window.location.href);
}

function normalizePath(value: string): string {
  if (!value) return "/";
  try {
    const url = new URL(value, "https://shockvaluemedia.com");
    return url.hash && url.pathname === "/" ? `/${url.hash}` : url.pathname;
  } catch {
    const [path] = value.split(/[?#]/);
    return path.startsWith("/") ? path : `/${path}`;
  }
}

function normalizeAnalyticsValue(value: string): string | undefined {
  const normalized = value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  return normalized || undefined;
}
